import { useEffect, useMemo, useState } from "react";
import { Download, Loader2, MessageCircle, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { api, fmtBRL, fmtDate, fmtPhone, labelOf, trackEvent, CLASSIFICATION_LABEL, STATUS_LABEL, formatApiError } from "@/lib/api";
import LeadDetail from "@/components/admin/LeadDetail";

const CLASS_STYLE = {
  quente: "bg-green-100 text-green-700",
  morno: "bg-amber-100 text-amber-700",
  frio: "bg-slate-200 text-slate-600",
  contato: "bg-brand-100 text-brand-700",
};

const ALL_FILTERS = [
  {
    key: "classification",
    testId: "filter-classification",
    placeholder: "Classificação",
    options: [
      { value: "all", label: "Todas as classificações" },
      { value: "quente", label: "Quente" },
      { value: "morno", label: "Morno" },
      { value: "frio", label: "Frio" },
      { value: "contato", label: "Contato (formulário)" },
    ],
  },
  {
    key: "category",
    testId: "filter-category",
    placeholder: "Categoria",
    options: [
      { value: "all", label: "Imóvel e veículo" },
      { value: "imovel", label: "Imóvel" },
      { value: "veiculo", label: "Veículo" },
    ],
  },
  {
    key: "status",
    testId: "filter-status",
    placeholder: "Status",
    options: [
      { value: "all", label: "Todos os status" },
      { value: "novo", label: "Novo" },
      { value: "contatado", label: "Contatado" },
      { value: "em_negociacao", label: "Em negociação" },
      { value: "fechado", label: "Fechado" },
      { value: "perdido", label: "Perdido" },
    ],
  },
  {
    key: "days",
    testId: "filter-days",
    placeholder: "Data",
    options: [
      { value: "all", label: "Qualquer data" },
      { value: "1", label: "Últimas 24 horas" },
      { value: "7", label: "Últimos 7 dias" },
      { value: "30", label: "Últimos 30 dias" },
    ],
  },
];

function waHrefFor(lead) {
  const cat = lead.category === "imovel" ? "imóvel" : lead.category === "veiculo" ? "veículo" : "consórcio";
  const msg = `Olá, ${lead.name}! Aqui é a Isabela, da Porto Seguro. Vi que você tem interesse em consórcio de ${cat}. Posso te ajudar com uma simulação?`;
  return `https://wa.me/55${String(lead.whatsapp).replace(/\D/g, "").slice(-11)}?text=${encodeURIComponent(msg)}`;
}

function NewBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-red-600" data-testid="lead-new-badge">
      <span className="h-1.5 w-1.5 rounded-full bg-red-500" /> Novo
    </span>
  );
}

function FilterSelect({ value, onChange, options, testId, placeholder }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="w-full min-w-0 rounded-full bg-white sm:w-auto sm:min-w-[9rem]">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function LeadsTable({ pendingLeadId, onPendingHandled }) {
  const [leads, setLeads] = useState(null);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({ classification: "all", category: "all", status: "all", days: "all" });

  const params = useMemo(() => {
    const p = {};
    if (filters.classification !== "all") p.classification = filters.classification;
    if (filters.category !== "all") p.category = filters.category;
    if (filters.status !== "all") p.status = filters.status;
    if (filters.days !== "all") p.days = filters.days;
    return p;
  }, [filters]);

  async function load() {
    try {
      const [l, s] = await Promise.all([api.get("/admin/leads", { params }), api.get("/admin/stats")]);
      setLeads(l.data);
      setStats(s.data);
    } catch (e) {
      if (e?.response?.status === 401) return;
      toast.error(formatApiError(e, "Erro ao carregar leads."));
      setLeads([]);
    }
  }

  useEffect(() => {
    load();
  }, [params]);

  useEffect(() => {
    if (pendingLeadId && leads && leads.length > 0) {
      const target = leads.find((l) => l.id === pendingLeadId);
      if (target) setSelected(target);
      onPendingHandled && onPendingHandled();
    }
  }, [pendingLeadId, leads]);

  const filtered = useMemo(() => {
    if (!leads) return null;
    const q = search.trim().toLowerCase().replace(/\D/g, "");
    if (!q) return leads;
    return leads.filter(
      (l) => l.name.toLowerCase().includes(q) || String(l.whatsapp).includes(q)
    );
  }, [leads, search]);

  async function exportCsv() {
    setExporting(true);
    try {
      const res = await api.get("/admin/leads/export.csv", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "leads-isabela.csv";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exportado!");
    } catch (e) {
      toast.error(formatApiError(e, "Erro ao exportar CSV."));
    } finally {
      setExporting(false);
    }
  }

  const statCards = stats
    ? [
        { label: "Total de leads", value: stats.total, cls: "text-slate-900" },
        { label: "Quentes", value: stats.by_classification.quente || 0, cls: "text-green-600" },
        { label: "Mornos", value: stats.by_classification.morno || 0, cls: "text-amber-600" },
        { label: "Frios", value: stats.by_classification.frio || 0, cls: "text-slate-500" },
        { label: "Últimas 24h", value: stats.last_24h, cls: "text-brand-600" },
      ]
    : [];

  return (
    <div className="space-y-6" data-testid="admin-leads-view">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {statCards.map((s) => (
          <div key={s.label} className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{s.label}</p>
            <p className={`mt-1 font-display text-2xl font-extrabold ${s.cls}`} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
              {s.value}
            </p>
          </div>
        ))}
      </div>

      <div className="space-y-3 rounded-3xl border border-slate-200 bg-white p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          {ALL_FILTERS.map((f) => (
            <FilterSelect
              key={f.key}
              testId={f.testId}
              placeholder={f.placeholder}
              options={f.options}
              value={filters[f.key]}
              onChange={(v) => setFilters((prev) => ({ ...prev, [f.key]: v }))}
            />
          ))}
          <div className="flex gap-2 lg:ml-auto">
            <button
              data-testid="leads-refresh-button"
              onClick={load}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2.5 text-xs font-bold text-slate-600 transition-colors hover:border-brand-600/40 hover:text-brand-600"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Atualizar
            </button>
            <button
              data-testid="leads-export-csv"
              onClick={exportCsv}
              disabled={exporting}
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-600 px-4 py-2.5 text-xs font-bold text-white transition-colors hover:bg-brand-700 disabled:opacity-60"
            >
              {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Exportar CSV
            </button>
          </div>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="leads-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome ou WhatsApp..."
            className="h-12 rounded-full pl-11"
          />
        </div>
      </div>

      {filtered === null ? (
        <div className="flex items-center justify-center rounded-3xl border border-slate-200 bg-white py-20 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-3xl border border-slate-200 bg-white py-20 text-center text-sm text-slate-400" data-testid="leads-empty">
          Nenhum lead encontrado com esses filtros.
        </div>
      ) : (
        <>
          {/* Cards mobile */}
          <div className="grid grid-cols-1 gap-4 lg:hidden" data-testid="leads-cards">
            {filtered.map((l) => (
              <div
                key={l.id}
                data-testid={`lead-card-${l.id}`}
                onClick={() => setSelected(l)}
                className={`cursor-pointer rounded-3xl border bg-white p-5 shadow-sm transition-colors ${l.status === "novo" ? "border-red-200 bg-red-50/40" : "border-slate-200"}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-display text-base font-bold text-slate-900">{l.name}</p>
                      {l.status === "novo" && <NewBadge />}
                    </div>
                    <p className="mt-0.5 text-xs font-semibold text-slate-500">{fmtDate(l.created_at)}</p>
                  </div>
                  <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${CLASS_STYLE[l.classification] || "bg-slate-100"}`}>
                    {CLASSIFICATION_LABEL[l.classification] || l.classification}
                    {l.score !== null && l.score !== undefined ? ` · ${l.score}` : ""}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <p className="text-slate-500">WhatsApp: <strong className="text-slate-800">{fmtPhone(l.whatsapp)}</strong></p>
                  <p className="text-slate-500">Categoria: <strong className="text-slate-800">{labelOf(l.category)}</strong></p>
                  <p className="text-slate-500">Subtipo: <strong className="text-slate-800">{labelOf(l.subtype)}</strong></p>
                  <p className="text-slate-500">Carta: <strong className="text-slate-800">{fmtBRL(l.credit_value)}</strong></p>
                  <p className="text-slate-500">Parcela: <strong className="text-slate-800">{fmtBRL(l.monthly_payment)}</strong></p>
                  <p className="text-slate-500">Lance: <strong className="text-slate-800">{labelOf(l.down_payment)}</strong></p>
                  <p className="text-slate-500">Prazo: <strong className="text-slate-800">{labelOf(l.timeline)}</strong></p>
                  <p className="text-slate-500">Status: <strong className="text-slate-800">{STATUS_LABEL[l.status] || l.status}</strong></p>
                </div>
                <div className="mt-4 flex gap-2">
                  <a
                    href={waHrefFor(l)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`lead-card-wa-${l.id}`}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-wa px-4 py-3 text-sm font-bold text-white"
                  >
                    <MessageCircle className="h-4 w-4" /> Conversar no WhatsApp
                  </a>
                  <button className="flex-1 rounded-full border border-slate-200 px-4 py-3 text-sm font-bold text-slate-700" data-testid={`lead-card-open-${l.id}`}>
                    Ver conversa
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Tabela desktop */}
          <div className="hidden overflow-hidden rounded-3xl border border-slate-200 bg-white lg:block" data-testid="leads-table">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1100px] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-400">
                    <th className="px-4 py-3">Data</th>
                    <th className="px-4 py-3">Nome</th>
                    <th className="px-4 py-3">WhatsApp</th>
                    <th className="px-4 py-3">Categoria</th>
                    <th className="px-4 py-3">Subtipo</th>
                    <th className="px-4 py-3">Carta</th>
                    <th className="px-4 py-3">Parcela</th>
                    <th className="px-4 py-3">Lance</th>
                    <th className="px-4 py-3">Prazo</th>
                    <th className="px-4 py-3">Score</th>
                    <th className="px-4 py-3">Classificação</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l) => (
                    <tr
                      key={l.id}
                      data-testid={`lead-row-${l.id}`}
                      onClick={() => setSelected(l)}
                      className={`cursor-pointer border-b border-slate-100 transition-colors last:border-0 hover:bg-brand-50/50 ${l.status === "novo" ? "bg-amber-50/60" : ""}`}
                    >
                      <td className="whitespace-nowrap px-4 py-3.5 text-slate-500">{fmtDate(l.created_at)}</td>
                      <td className="px-4 py-3.5 font-bold text-slate-800">
                        <span className="flex items-center gap-2">{l.name} {l.status === "novo" && <NewBadge />}</span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3.5 text-slate-600">{fmtPhone(l.whatsapp)}</td>
                      <td className="px-4 py-3.5 text-slate-600">{labelOf(l.category)}</td>
                      <td className="px-4 py-3.5 text-slate-600">{labelOf(l.subtype)}</td>
                      <td className="whitespace-nowrap px-4 py-3.5 text-slate-600">{fmtBRL(l.credit_value)}</td>
                      <td className="whitespace-nowrap px-4 py-3.5 text-slate-600">{fmtBRL(l.monthly_payment)}</td>
                      <td className="whitespace-nowrap px-4 py-3.5 text-slate-600">{labelOf(l.down_payment)}</td>
                      <td className="whitespace-nowrap px-4 py-3.5 text-slate-600">{labelOf(l.timeline)}</td>
                      <td className="px-4 py-3.5 font-bold text-slate-800">{l.score ?? "—"}</td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-bold capitalize ${CLASS_STYLE[l.classification] || "bg-slate-100"}`}>
                          {CLASSIFICATION_LABEL[l.classification] || l.classification}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-slate-600">{STATUS_LABEL[l.status] || l.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="lead-detail-sheet">
          {selected && <LeadDetail lead={selected} onRefresh={load} onClose={() => setSelected(null)} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
